import {Component, OnInit, OnDestroy, NgZone, ChangeDetectionStrategy} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { SelectedStoreState } from '../+state/stores.reducer';
import { getSelectedStore } from '../+state/stores.selectors';

import { LocationService } from '../../location.service';

import { LoadStore,
  SaveStoreByAlias,
  SaveStoreByAliasFailed,
  SaveStoreByAliasSuccess,
  StoreActionType,
  SetMobileStore
} from '../+state/stores.actions';

import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofType } from '@ngrx/effects';
import { HelperService } from '../../helper.service';
import { StoreService } from '../store.service';
import { StoreSaveMode } from 'src/app/stores/stores';
import { WhitelabelService } from 'src/app/shared/whitelabel.service';

@Component({
  selector: 'app-store-sibling-selector',
  templateUrl: './store-sibling-selector.component.html',
  styleUrls: ['./store-sibling-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreSiblingSelectorComponent implements OnInit, OnDestroy {

  siblings: any[] = [];
  selectedSibling: {
    storeId: number;
    storeAlias: string | null;
    storeName: string | null;
    settings: any;
  } = {
    storeId: 0,
    storeAlias: null,
    storeName: null,
    settings: null,
  };
  unsubscribe$: Subject<void> = new Subject<void>();
  host: string;

  constructor(
    private store: Store<SelectedStoreState>,
    private locationService: LocationService,
    private storeService: StoreService,
    private router: Router,
    private toastr: ToastrService,
    private translateSer: TranslateService,
    private actions$: Actions,
    private zone: NgZone,
    private helper: HelperService,
    private whitelabelService: WhitelabelService
  ) { }

  ngOnInit(): void {
    this.store
      .select(getSelectedStore)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(store => !!store?.id)
      )
      .subscribe((store) => {
        this.selectedSibling = {
          storeId: store.id,
          storeAlias: store.aliasName,
          storeName: store.name,
          settings: { ...(store.settings ?? {}) }
        };
        this.siblings = [
          this.selectedSibling,
          ...store.relation.siblingStores.filter((sibling) => !sibling.isIndependent),
        ];
      });
    this.host = this.whitelabelService.getHost();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  siblingSelected() {
    if (this.helper.isMobileApp()) {
      this.actions$.pipe(
        ofType<SaveStoreByAliasSuccess>(StoreActionType.SaveStoreByAliasSuccess),
        filter(action => action.saveMode === StoreSaveMode.URL),
        take(1),
      ).subscribe(action => {
        if (this.locationService.isOrderCapture()) {
          this.router.navigateByUrl(`/manager/stores/${this.selectedSibling.storeId}/capture/${this.selectedSibling.storeAlias}`);
          this.store.dispatch(new LoadStore(this.selectedSibling.storeAlias));
          this.locationService.setOrderUuid(null);
        } else {
          if (action.storeAlias) {
            if (this.helper.isGoApp()) {
              this.router.navigateByUrl('/', { skipLocationChange: true, onSameUrlNavigation: 'reload' }).then(() => {
                this.store.dispatch(new SetMobileStore(action.storeAlias));
                if (environment.templateStoreAlias !== action.storeAlias) {
                  environment.templateStoreAlias = action.storeAlias;
                  let storeUrl = `https://${action.storeAlias}.${this.host}`;
                  if(this.selectedSibling.settings.EXTERNAL_DOMAIN){
                    storeUrl = `https://${this.selectedSibling.settings.EXTERNAL_DOMAIN}`
                  }
                  this.store.dispatch(new SaveStoreByAlias(action.storeAlias, storeUrl, StoreSaveMode.URL));
                  this.router.navigateByUrl(`/public/customer/store/${action.storeAlias}`);
                }
              });

            } else if (this.helper.isGoTemplateApp()) {

              if (environment.templateStoreAlias !== action.storeAlias) {
                environment.templateStoreAlias = action.storeAlias;
                this.store.dispatch(new SetMobileStore(action.storeAlias));

                this.router.navigateByUrl('/', { skipLocationChange: true, onSameUrlNavigation: 'reload' }).then(() => {
                  this.router.navigateByUrl(`/public/${action.storeAlias}`);
                });
              }
            }
          }
        }
      });
      this.actions$.pipe(
        ofType<SaveStoreByAliasFailed>(StoreActionType.SaveStoreByAliasFailed),
        take(1),
      ).subscribe(action => {
        this.zone.run(() => this.toastr.info(this.translateSer.instant('public.main.storeNotFound'), this.translateSer.instant('public.global.information')));
      });
      let storeUrl = `https://${this.selectedSibling.storeAlias}.${this.host}`;
      if(this.selectedSibling.settings.EXTERNAL_DOMAIN){
        storeUrl = `https://${this.selectedSibling.settings.EXTERNAL_DOMAIN}`
      }
      this.store.dispatch(new SaveStoreByAlias(this.selectedSibling.storeAlias, storeUrl, StoreSaveMode.URL));
    } else {
      if (this.locationService.isOrderCapture()) {
        const targetUrl = `/manager/stores/${this.selectedSibling.storeId}/capture/${this.selectedSibling.storeAlias}`;
        if (this.router.url !== targetUrl) {
          this.router.navigateByUrl(targetUrl);
          this.store.dispatch(new LoadStore(this.selectedSibling.storeAlias));
          this.locationService.setOrderUuid(null);
        }
      } else {
        window.location.href = this.storeService.getSiblingUrl(this.selectedSibling.storeId,
                                                               this.selectedSibling.storeAlias,
                                                               this.whitelabelService.getWhiteLabelDomainUri(),
                                                               this.whitelabelService.getAdminHostURL(),
                                                               this.selectedSibling?.settings?.EXTERNAL_DOMAIN);
      }
    }
  }
}
